import ValidatedForm, { FormReadiness, SubmitButton } from '../forms/ValidatedForm';
import FieldLabel, { RequiredFieldsNote } from '../forms/FieldLabel';
import { useEffect, useMemo, useRef, useState } from "react";
import {
  PRESSKIT_LIMITS,
  deletePresskitAsset,
  fetchAdminPresskitAssets,
  formatFileSize,
  reorderPresskitAssets,
  replacePresskitAsset,
  updatePresskitAsset,
  uploadPresskitAsset,
} from "../../api/presskit";
import PresskitMediaCard from "./PresskitMediaCard";
import AdminNotice from './AdminNotice';
import { presskitAcceptByType } from "./presskitMediaConfig";

export default function PresskitMediaManager() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [uploadProgress, setUploadProgress] = useState(null);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [dragState, setDragState] = useState(null);
  const measuredIds = useRef(new Set());

  const groupedAssets = useMemo(() => {
    const groups = {
      image: assets.filter((asset) => asset.mediaType === "image").sort((a, b) => a.displayOrder - b.displayOrder),
      video: assets.filter((asset) => asset.mediaType === "video").sort((a, b) => a.displayOrder - b.displayOrder),
    };

    if (dragState) {
      const assetsById = new Map(groups[dragState.mediaType].map((asset) => [asset.id, asset]));
      groups[dragState.mediaType] = dragState.previewIds.map((id) => assetsById.get(id)).filter(Boolean);
    }

    return groups;
  }, [assets, dragState]);

  useEffect(() => {
    fetchAdminPresskitAssets()
      .then(setAssets)
      .catch((error) => setStatus({ type: "error", message: error.message }))
      .finally(() => setLoading(false));
  }, []);

  const replaceAssetInState = (updated) => {
    setAssets((current) => current.map((asset) => asset.id === updated.id ? updated : asset));
  };

  async function handleUpload(event) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const file = data.get("media");
    const altText = data.get("description");
    setBusy("upload");
    setUploadProgress(0);
    setStatus({ type: "", message: "" });
    try {
      const mediaType = file.type.startsWith("video/") ? "video" : "image";
      const displayOrder = Math.max(0, ...groupedAssets[mediaType].map((asset) => asset.displayOrder)) + 10;
      const created = await uploadPresskitAsset({ file, altText, displayOrder, onProgress: setUploadProgress });
      setAssets((current) => [...current, created]);
      form.reset();
      setStatus({ type: "success", message: `${created.mediaType === "image" ? "Photo" : "Video"} uploaded and published.` });
    } catch (error) {
      setStatus({ type: "error", message: error.message });
    } finally {
      setBusy("");
      setUploadProgress(null);
    }
  }

  async function handleDescription(asset, altText) {
    setBusy(asset.id);
    setStatus({ type: "", message: "" });
    try {
      const updated = await updatePresskitAsset(asset.id, { altText: altText.trim() });
      replaceAssetInState(updated);
      setStatus({ type: "success", message: "Description saved." });
    } catch (error) {
      setStatus({ type: "error", message: error.message });
    } finally {
      setBusy("");
    }
  }

  async function handleVisibility(asset) {
    setBusy(asset.id);
    setStatus({ type: "", message: "" });
    try {
      const updated = await updatePresskitAsset(asset.id, { isVisible: !asset.isVisible });
      replaceAssetInState(updated);
      setStatus({ type: "success", message: updated.isVisible ? "Media published." : "Media hidden from the public Press Kit." });
    } catch (error) {
      setStatus({ type: "error", message: error.message });
    } finally {
      setBusy("");
    }
  }

  async function handleReplace(asset, file) {
    setBusy(asset.id);
    setUploadProgress(0);
    setStatus({ type: "", message: "" });
    try {
      const updated = await replacePresskitAsset(asset, file, setUploadProgress);
      replaceAssetInState(updated);
      measuredIds.current.delete(asset.id);
      setStatus({ type: "success", message: `${asset.mediaType === "image" ? "Photo" : "Video"} replaced.` });
    } catch (error) {
      setStatus({ type: "error", message: error.message });
    } finally {
      setBusy("");
      setUploadProgress(null);
    }
  }

  async function handleDelete(asset) {
    if (!window.confirm(`Permanently delete “${asset.altText}”? This cannot be undone.`)) return;
    setBusy(asset.id);
    setStatus({ type: "", message: "" });
    try {
      await deletePresskitAsset(asset);
      setAssets((current) => current.filter((item) => item.id !== asset.id));
      setStatus({ type: "success", message: "Media permanently deleted." });
    } catch (error) {
      setStatus({ type: "error", message: error.message });
    } finally {
      setBusy("");
    }
  }

  async function applyOrder(mediaType, orderedGroup) {
    const previous = assets;
    const orderById = new Map(orderedGroup.map((asset, index) => [asset.id, (index + 1) * 10]));
    setAssets((current) => current.map((asset) => orderById.has(asset.id)
      ? { ...asset, displayOrder: orderById.get(asset.id) }
      : asset));
    setBusy(`reorder-${mediaType}`);
    setStatus({ type: "", message: "" });
    try {
      await reorderPresskitAssets(orderedGroup.map((asset) => asset.id));
      setStatus({ type: "success", message: `${mediaType === "image" ? "Photo" : "Video"} order saved.` });
    } catch (error) {
      setAssets(previous);
      setStatus({ type: "error", message: error.message });
    } finally {
      setBusy("");
    }
  }

  function handleMove(asset, direction) {
    const group = [...groupedAssets[asset.mediaType]];
    const fromIndex = group.findIndex((item) => item.id === asset.id);
    const toIndex = fromIndex + direction;
    if (fromIndex < 0 || toIndex < 0 || toIndex >= group.length) return;
    const [moved] = group.splice(fromIndex, 1);
    group.splice(toIndex, 0, moved);
    applyOrder(asset.mediaType, group);
  }

  function handleDragStart(asset, event) {
    const originalIds = groupedAssets[asset.mediaType].map((item) => item.id);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", asset.id);
    setDragState({
      id: asset.id,
      mediaType: asset.mediaType,
      originalIds,
      previewIds: originalIds,
    });
  }

  function handleDragEnter(target) {
    setDragState((current) => {
      if (!current || current.mediaType !== target.mediaType || current.id === target.id) return current;
      const fromIndex = current.previewIds.indexOf(current.id);
      const toIndex = current.previewIds.indexOf(target.id);
      if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return current;
      const previewIds = [...current.previewIds];
      const [movedId] = previewIds.splice(fromIndex, 1);
      previewIds.splice(toIndex, 0, movedId);
      return { ...current, previewIds };
    });
  }

  function handleDragEnd() {
    setDragState(null);
  }

  function handleDrop(event) {
    event.preventDefault();
    if (!dragState) return;
    const { mediaType, originalIds, previewIds } = dragState;
    setDragState(null);
    if (previewIds.every((id, index) => id === originalIds[index])) return;
    const assetsById = new Map(assets.map((asset) => [asset.id, asset]));
    applyOrder(mediaType, previewIds.map((id) => assetsById.get(id)).filter(Boolean));
  }

  function handleDimensions(asset, dimensions) {
    if (asset.width && asset.height) return;
    if (!dimensions.width || !dimensions.height || measuredIds.current.has(asset.id)) return;
    measuredIds.current.add(asset.id);
    replaceAssetInState({ ...asset, ...dimensions });
    updatePresskitAsset(asset.id, dimensions)
      .then(replaceAssetInState)
      .catch(() => measuredIds.current.delete(asset.id));
  }

  return (
    <section className="admin-media-manager" aria-labelledby="presskit-media-heading">
      <div className="admin-media-heading">
        <div>
          <p className="admin-kicker">Press Kit</p>
          <h2 id="presskit-media-heading">Approved media</h2>
          <p>Drag a photo or video preview to see the new order, then release to save. Hidden media remains stored but is not shown publicly.</p>
        </div>
        <a href="/presskit" target="_blank" rel="noopener noreferrer">View Press Kit</a>
      </div>

      <AdminNotice text={status.message} type={status.type} onDismiss={() => setStatus({ type: '', message: '' })} />
      {uploadProgress !== null && (
        <div className="admin-upload-progress" role="status" aria-live="polite">
          <span>Uploading media: {uploadProgress}%</span>
          <progress value={uploadProgress} max="100">{uploadProgress}%</progress>
        </div>
      )}

      <ValidatedForm className="admin-media-upload" onSubmit={handleUpload}>
        <RequiredFieldsNote />
        <FieldLabel>
          Photo or video
          <input name="media" type="file" accept={`${presskitAcceptByType.image},${presskitAcceptByType.video}`} required />
        </FieldLabel>
        <FieldLabel>
          Descriptive alt text or video description
          <input name="description" maxLength="500" required />
        </FieldLabel>
        <SubmitButton className="admin-primary" disabled={Boolean(busy)}>{busy === "upload" ? "Uploading…" : "Upload and publish"}</SubmitButton>
        <FormReadiness />
        <small>Photos: JPEG, PNG, or WebP up to {formatFileSize(PRESSKIT_LIMITS.image)}. Videos: MP4 or WebM up to {formatFileSize(PRESSKIT_LIMITS.video)}.</small>
      </ValidatedForm>

      {loading ? <p className="admin-loading">Loading approved media…</p> : (
        [
          ["image", "Photos"],
          ["video", "Videos"],
        ].map(([mediaType, heading]) => (
          <section className="admin-media-section" key={mediaType}>
            <h3>{heading} <span>{groupedAssets[mediaType].length}</span></h3>
            {groupedAssets[mediaType].length === 0 ? (
              <p className="admin-media-empty">No approved {heading.toLowerCase()} yet.</p>
            ) : (
              <div
                className={`admin-media-grid admin-sortable-list${dragState?.mediaType === mediaType ? " is-sorting" : ""}`}
                onDragOver={(event) => {
                  if (dragState?.mediaType === mediaType) {
                    event.preventDefault();
                    event.dataTransfer.dropEffect = "move";
                  }
                }}
                onDrop={handleDrop}
              >
                {groupedAssets[mediaType].map((asset, index) => (
                  <PresskitMediaCard
                    key={asset.id}
                    asset={asset}
                    index={index}
                    total={groupedAssets[mediaType].length}
                    busy={Boolean(busy)}
                    dragging={dragState?.id === asset.id}
                    onDelete={handleDelete}
                    onDescription={handleDescription}
                    onDimensions={handleDimensions}
                    onDragEnd={handleDragEnd}
                    onDragEnter={handleDragEnter}
                    onDragStart={handleDragStart}
                    onMove={handleMove}
                    onReplace={handleReplace}
                    onVisibility={handleVisibility}
                  />
                ))}
                {dragState?.mediaType === mediaType && (
                  <p className="admin-drag-status" role="status" aria-live="polite">
                    New position {dragState.previewIds.indexOf(dragState.id) + 1} of {dragState.previewIds.length}. Release to save.
                  </p>
                )}
              </div>
            )}
          </section>
        ))
      )}
    </section>
  );
}
